import React, { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { pode } from '../../authorization/pode';
import { useVeredito } from '../../authorization/useVeredito';
import { useListaPermitida, type ListaGuardada } from '../../authorization/useListaPermitida';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { employeeService } from '../../services/employeeService';
import { occupationService } from '../../services/occupationService';
import { unitService } from '../../services/unitService';
import { NIVEIS_DE_APLICACAO, ROTULO_DO_NIVEL, type NivelDeAplicacao } from '../../types/nivelDeAplicacao';
import { mesmoId } from './textosDoNivel';
import type { DestinoRef } from './tipos';

export interface EscolhaDeOnde {
  nivel: NivelDeAplicacao | null;
  companyId: number | null;
  departmentId: number | null;
  occupationId: number | null;
  /** Um destino por vez: o registro escolhido no último nível. */
  destino: DestinoRef | null;
}

interface Opcao {
  id: number;
  nome: string;
}

/** A resposta de uma lista filha, com o pai de que ela veio. */
interface RespostaDoPai {
  de: number;
  opcoes: Opcao[];
}

interface ParaQuemProps {
  escolha: EscolhaDeOnde;
  aoMudar: (escolha: EscolhaDeOnde) => void;
}

/**
 * A lista filha vale só para o pai de que veio. A lista vazia só se afirma com o pai escolhido e
 * depois que a busca dele voltou (#604/#605): sem pai, enquanto carrega ou com a resposta de
 * outro pai, o seletor fica calado em vez de dizer que falta cadastro.
 */
function doPai(lista: ListaGuardada<RespostaDoPai>, paiId: number | null): { opcoes: Opcao[]; vazia: boolean } {
  const [resposta] = lista.itens;
  const daqui = paiId !== null && lista.resolvida && resposta !== undefined && resposta.de === paiId;
  const opcoes = daqui ? resposta.opcoes : [];
  return { opcoes, vazia: daqui && opcoes.length === 0 };
}

async function carregarEmpresas(): Promise<Opcao[]> {
  const resposta = await companyService.getCompanies({ limit: 1000 });
  return resposta.data.map((c) => ({ id: Number(c.id), nome: c.tradeName || c.legalName }));
}

function SemCadastro({ texto, rotuloDoLink, para, liberado }: { texto: string; rotuloDoLink: string; para: string; liberado: boolean }) {
  return (
    <FormHelperText>
      {texto}
      {liberado && (
        <>
          {' '}
          <Link component={RouterLink} to={para}>
            {rotuloDoLink}
          </Link>
        </>
      )}
    </FormHelperText>
  );
}

const LARGURA_DO_CAMPO = { xs: '100%', sm: 220 };

interface SeletorProps {
  rotulo: string;
  valor: number | null;
  opcoes: Opcao[];
  aoEscolher: (id: number) => void;
  desabilitado: boolean;
  aviso?: React.ReactNode;
}

function Seletor({ rotulo, valor, opcoes, aoEscolher, desabilitado, aviso }: SeletorProps) {
  const idDoRotulo = `para-quem-${rotulo.toLowerCase()}`;
  const conhecido = valor !== null && opcoes.some((opcao) => mesmoId(opcao.id, valor));
  return (
    <FormControl size="small" sx={{ width: LARGURA_DO_CAMPO }}>
      <InputLabel id={idDoRotulo}>{rotulo}</InputLabel>
      <Select
        labelId={idDoRotulo}
        label={rotulo}
        value={conhecido ? valor : ''}
        onChange={(e) => aoEscolher(Number(e.target.value))}
        disabled={desabilitado || opcoes.length === 0}
      >
        {opcoes.map((opcao) => (
          <MenuItem key={opcao.id} value={opcao.id}>
            {opcao.nome}
          </MenuItem>
        ))}
      </Select>
      {aviso}
    </FormControl>
  );
}

interface SeletorDoDestinoProps {
  rotulo: string;
  nivel: NivelDeAplicacao;
  opcoes: Opcao[];
  escolhido: DestinoRef | null;
  aoMudar: (destino: DestinoRef | null) => void;
  desabilitado?: boolean;
  aviso?: React.ReactNode;
}

/** O último nível: um destino por vez, com busca, porque a lista pode ser longa (funcionários). */
function SeletorDoDestino({ rotulo, nivel, opcoes, escolhido, aoMudar, desabilitado = false, aviso }: SeletorDoDestinoProps) {
  const valor = escolhido ? (opcoes.find((opcao) => mesmoId(opcao.id, escolhido.id)) ?? null) : null;
  return (
    <Box sx={{ minWidth: { sm: 280 }, flex: { sm: '1 1 280px' } }}>
      <Autocomplete
        size="small"
        options={opcoes}
        value={valor}
        disabled={desabilitado || opcoes.length === 0}
        getOptionLabel={(opcao) => opcao.nome}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_e, nova) => aoMudar(nova ? { nivel, id: nova.id, nome: nova.nome } : null)}
        renderInput={(parametros) => <TextField {...parametros} label={rotulo} />}
      />
      {aviso}
    </Box>
  );
}

/**
 * O "Para quem?" da tela de aplicação (#606): nível, empresa, setor e o destino, em linha. Um
 * destino por vez. Cada lista pergunta antes de pedir; cada seletor vazio diz o que falta e
 * oferece o cadastro a quem pode — a regra da #604/#605.
 */
export const ParaQuem: React.FC<ParaQuemProps> = ({ escolha, aoMudar }) => {
  const { nivel, companyId, departmentId, occupationId, destino } = escolha;
  const cadastrarUnidade = useVeredito('/units', pode('unit.create'));
  const cadastrarSetor = useVeredito('/departments/create', pode('department.create'));
  const cadastrarCargo = useVeredito('/occupations/create', pode('occupation.create'));
  const cadastrarFuncionario = useVeredito('/employees/new', pode('employee.create'));

  // Empresas pedem `company.read`, que a porta da tela não exige: sem ela o seletor diz por quê.
  const empresasGuardadas = useListaPermitida('company.read', 'Empresas', carregarEmpresas);
  const empresas = [...empresasGuardadas.itens];
  const avisoSemEmpresas =
    empresasGuardadas.resolvida && !empresasGuardadas.permitido ? (
      <FormHelperText>Seu perfil não pode ver as empresas.</FormHelperText>
    ) : undefined;

  const usaSetor = nivel === 'department' || nivel === 'occupation' || nivel === 'employee';
  const usaCargo = nivel === 'occupation' || nivel === 'employee';
  const nivelUnidade = nivel === 'unit';
  const empresaDosSetores = usaSetor ? companyId : null;
  const setorDosCargos = usaCargo ? departmentId : null;
  const cargoDosFuncionarios = nivel === 'employee' ? occupationId : null;
  const empresaDasUnidades = nivelUnidade ? companyId : null;

  const carregarSetores = useCallback(async (): Promise<RespostaDoPai[]> => {
    if (empresaDosSetores === null) return [];
    const resposta = await departmentService.getDepartments({ companyId: String(empresaDosSetores), limit: 1000 });
    return [{ de: empresaDosSetores, opcoes: resposta.data.map((d) => ({ id: Number(d.id), nome: d.name })) }];
  }, [empresaDosSetores]);
  const carregarCargos = useCallback(async (): Promise<RespostaDoPai[]> => {
    if (setorDosCargos === null) return [];
    const resposta = await occupationService.getOccupations({ limit: 1000, filters: { departmentId: setorDosCargos } });
    return [{ de: setorDosCargos, opcoes: resposta.data.map((o) => ({ id: Number(o.id), nome: o.name })) }];
  }, [setorDosCargos]);
  const carregarFuncionarios = useCallback(async (): Promise<RespostaDoPai[]> => {
    if (cargoDosFuncionarios === null) return [];
    const resposta = await employeeService.getEmployees({ limit: 1000, filters: { occupationId: cargoDosFuncionarios } });
    return [{ de: cargoDosFuncionarios, opcoes: resposta.data.map((e) => ({ id: Number(e.id), nome: e.name })) }];
  }, [cargoDosFuncionarios]);
  const carregarUnidades = useCallback(async (): Promise<RespostaDoPai[]> => {
    if (empresaDasUnidades === null) return [];
    const unidades = await unitService.getUnitsByCompanyId(empresaDasUnidades);
    return [{ de: empresaDasUnidades, opcoes: unidades.map((u) => ({ id: Number(u.id), nome: u.name })) }];
  }, [empresaDasUnidades]);

  const setoresGuardados = useListaPermitida('department.read', 'Setores', carregarSetores, {
    habilitado: empresaDosSetores !== null,
  });
  const cargosGuardados = useListaPermitida('occupation.read', 'Cargos', carregarCargos, {
    habilitado: setorDosCargos !== null,
  });
  const funcionariosGuardados = useListaPermitida('employee.read', 'Funcionários', carregarFuncionarios, {
    habilitado: cargoDosFuncionarios !== null,
  });
  const unidadesGuardadas = useListaPermitida('unit.read', 'Unidades', carregarUnidades, {
    habilitado: empresaDasUnidades !== null,
  });
  const setores = doPai(setoresGuardados, empresaDosSetores);
  const cargos = doPai(cargosGuardados, setorDosCargos);
  const funcionarios = doPai(funcionariosGuardados, cargoDosFuncionarios);
  const unidades = doPai(unidadesGuardadas, empresaDasUnidades);

  const mudar = (parcial: Partial<EscolhaDeOnde>) => aoMudar({ ...escolha, ...parcial });
  const mudarDestino = (novo: DestinoRef | null) => mudar({ destino: novo });

  /** A recusa de leitura diz por que o seletor ficou vazio; a lista vazia, o que falta cadastrar. */
  const aviso = (guardada: ListaGuardada<RespostaDoPai>, paiId: number | null, vazia: React.ReactNode) =>
    paiId !== null && guardada.resolvida && !guardada.permitido ? (
      <FormHelperText>{guardada.bloqueio}</FormHelperText>
    ) : (
      vazia
    );

  const avisoSemSetor = aviso(
    setoresGuardados,
    empresaDosSetores,
    setores.vazia && (
      <SemCadastro
        texto="Esta empresa não tem setores cadastrados."
        rotuloDoLink="Cadastrar setor"
        para="/departments/create"
        liberado={cadastrarSetor.liberado}
      />
    )
  );
  const avisoSemCargo = aviso(
    cargosGuardados,
    setorDosCargos,
    cargos.vazia && (
      <SemCadastro
        texto="Este setor não tem cargos cadastrados."
        rotuloDoLink="Cadastrar cargo"
        para="/occupations/create"
        liberado={cadastrarCargo.liberado}
      />
    )
  );
  const avisoSemFuncionario = aviso(
    funcionariosGuardados,
    cargoDosFuncionarios,
    funcionarios.vazia && (
      <SemCadastro
        texto="Este cargo não tem funcionários cadastrados."
        rotuloDoLink="Cadastrar funcionário"
        para="/employees/new"
        liberado={cadastrarFuncionario.liberado}
      />
    )
  );
  const avisoDasUnidades = aviso(
    unidadesGuardadas,
    empresaDasUnidades,
    unidades.vazia && (
      <SemCadastro
        texto="Esta empresa não tem unidades cadastradas."
        rotuloDoLink="Cadastrar unidade"
        para="/units"
        liberado={cadastrarUnidade.liberado}
      />
    )
  );

  return (
    <Box component="section" aria-labelledby="para-quem-titulo">
      <Typography id="para-quem-titulo" variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        Para quem?
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap flexWrap="wrap" alignItems="flex-start">
        <FormControl size="small" sx={{ width: LARGURA_DO_CAMPO }}>
          <InputLabel id="para-quem-nivel">Nível</InputLabel>
          <Select
            labelId="para-quem-nivel"
            label="Nível"
            value={nivel ?? ''}
            onChange={(e) => mudar({ nivel: e.target.value as NivelDeAplicacao, destino: null })}
          >
            {NIVEIS_DE_APLICACAO.map((opcao) => (
              <MenuItem key={opcao} value={opcao}>
                {ROTULO_DO_NIVEL[opcao]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {nivel === 'company' ? (
          <SeletorDoDestino
            rotulo="Empresa"
            nivel="company"
            opcoes={empresas}
            escolhido={destino}
            aoMudar={mudarDestino}
            aviso={avisoSemEmpresas}
          />
        ) : (
          <Box>
            <Seletor
              rotulo="Empresa"
              valor={companyId}
              opcoes={empresas}
              desabilitado={false}
              aoEscolher={(id) => mudar({ companyId: id, departmentId: null, occupationId: null, destino: null })}
            />
            {avisoSemEmpresas}
          </Box>
        )}

        {nivelUnidade && companyId !== null && (
          <SeletorDoDestino
            rotulo="Unidade"
            nivel="unit"
            opcoes={unidades.opcoes}
            escolhido={destino}
            aoMudar={mudarDestino}
            aviso={avisoDasUnidades}
          />
        )}

        {nivel === 'department' && (
          <SeletorDoDestino
            rotulo="Setor"
            nivel="department"
            opcoes={setores.opcoes}
            escolhido={destino}
            aoMudar={mudarDestino}
            desabilitado={companyId === null}
            aviso={avisoSemSetor}
          />
        )}
        {usaCargo && (
          <Seletor
            rotulo="Setor"
            valor={departmentId}
            opcoes={setores.opcoes}
            desabilitado={companyId === null}
            aoEscolher={(id) => mudar({ departmentId: id, occupationId: null, destino: null })}
            aviso={avisoSemSetor}
          />
        )}

        {nivel === 'occupation' && (
          <SeletorDoDestino
            rotulo="Cargo"
            nivel="occupation"
            opcoes={cargos.opcoes}
            escolhido={destino}
            aoMudar={mudarDestino}
            desabilitado={departmentId === null}
            aviso={avisoSemCargo}
          />
        )}
        {nivel === 'employee' && (
          <>
            <Seletor
              rotulo="Cargo"
              valor={occupationId}
              opcoes={cargos.opcoes}
              desabilitado={departmentId === null}
              aoEscolher={(id) => mudar({ occupationId: id, destino: null })}
              aviso={avisoSemCargo}
            />
            <SeletorDoDestino
              rotulo="Funcionário"
              nivel="employee"
              opcoes={funcionarios.opcoes}
              escolhido={destino}
              aoMudar={mudarDestino}
              desabilitado={occupationId === null}
              aviso={avisoSemFuncionario}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};
